// src/pages/Archive/TechnicianReports.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Button,
  Stack, TextField, Grid, IconButton, Tooltip
} from '@mui/material';
import { 
  Visibility, 
  Download, 
  Delete, 
  Search,
  Refresh
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es, uk, ca, enGB } from 'date-fns/locale';
import { useI18n } from '../../i18n/i18n';
import { getData, saveData } from '../../services/storageService';

const TechnicianReports = () => {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const locales = { es, uk, ca, en: enGB };

  useEffect(() => {
    loadReports();
  }, []);

  useEffect(() => {
    filterReports();
  }, [reports, searchTerm, statusFilter, dateRange]);

  const loadReports = async () => {
    try {
      const techReports = await getData('technician_reports') || [];
      if (techReports.length === 0) {
        const demoReports = generateDemoReports();
        setReports(demoReports);
        await saveData('technician_reports', demoReports);
      } else {
        setReports(techReports);
      }
    } catch (error) {
      console.error('Error loading technician reports:', error);
      setReports(generateDemoReports());
    }
  };

  const generateDemoReports = () => {
    return [
      {
        id: 'tech_001',
        date: '2024-01-15',
        createdBy: 'Juan García',
        status: 'completed',
        equipmentCount: 8,
        sections: {
          calderas: { hora: '08:30', nivel_agua: '85%', presion: '2.5' },
          introductor: { via1_val1: '45', via1_val2: '38' },
          hibrida: { piezasGrandes: '23', trabajador_via1: 'Maria Lopez' },
          kannegiesser: [{ p1: '15', p2: '12', p3: '18', p4: '14' }]
        },
        notes: 'Todo el equipo funcionando correctamente.'
      },
      {
        id: 'tech_002', 
        date: '2024-01-16',
        createdBy: 'Maria Lopez',
        status: 'in-progress',
        equipmentCount: 5,
        sections: {
          calderas: { hora: '14:20', nivel_agua: '90%', presion: '2.8' },
          introductor: { via1_val1: '52', via1_val2: '41' },
          hibrida: { piezasGrandes: '18', trabajador_via1: 'Carlos Ruiz' },
          kannegiesser: [{ p1: '12', p2: '15', p3: '14', p4: '16' }]
        },
        notes: 'En proceso de mantenimiento preventivo.'
      },
      {
        id: 'tech_003',
        date: '2024-01-17', 
        createdBy: 'Carlos Ruiz',
        status: 'pending',
        equipmentCount: 3,
        sections: {
          calderas: { hora: '10:15', nivel_agua: '80%', presion: '2.3' },
          introductor: { via1_val1: '48', via1_val2: '39' },
          hibrida: { piezasGrandes: '25', trabajador_via1: 'Ana Martinez' },
          kannegiesser: [{ p1: '18', p2: '14', p3: '16', p4: '12' }]
        },
        notes: 'Esperando repuestos para reparación.'
      }
    ];
  };

  const filterReports = () => {
    let filtered = reports;
    if (searchTerm) {
      filtered = filtered.filter(report =>
        report.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.createdBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        report.status?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(report => report.status === statusFilter);
    }
    if (dateRange.from) {
      filtered = filtered.filter(report => report.date >= dateRange.from);
    }
    if (dateRange.to) {
      filtered = filtered.filter(report => report.date <= dateRange.to);
    }
    setFilteredReports(filtered);
  };

  const handleDelete = async (reportId) => {
    if (window.confirm(t('confirm_delete_record'))) {
      try {
        const updatedReports = reports.filter(report => report.id !== reportId);
        setReports(updatedReports);
        await saveData('technician_reports', updatedReports);
      } catch (error) {
        console.error('Error deleting report:', error);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'warning';
      case 'pending': return 'error';
      default: return 'default';
    }
  };

  const handleViewReport = (reportId) => {
    navigate(`/technician-report/${reportId}`);
  };

  const handleDownloadPDF = (reportId) => {
    // Генерація PDF
    console.log('Generating PDF for report:', reportId);
    alert(`${t('actions_download_pdf')}: ${reportId}\n${t('export_development')}`);
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'PPP', { locale: locales[lang] });
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {t('archive.tabs.technician')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t('archive.subtitle')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={loadReports}
            >
              {t('common_refresh')}
            </Button>
          </Stack>

          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t('archive.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label={t('archive.filters.status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="all">{t('archive.filters.all')}</option>
                <option value="completed">{t('status_completed')}</option>
                <option value="in-progress">{t('status_in-progress')}</option>
                <option value="pending">{t('status_pending')}</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label={t('filters_from')}
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label={t('filters_to')}
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Results Info */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('archive.total_found')}: <strong>{filteredReports.length}</strong>
            </Typography>
            <Button
              variant="outlined"
              onClick={() => alert(t('export_development'))}
            >
              {t('actions_export_excel')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('archive.table.id')}</strong></TableCell>
                  <TableCell><strong>{t('labels_date')}</strong></TableCell>
                  <TableCell><strong>{t('common_technician')}</strong></TableCell>
                  <TableCell><strong>{t('archive.table.status')}</strong></TableCell>
                  <TableCell><strong>{t('common_equipment')}</strong></TableCell>
                  <TableCell><strong>{t('archive.table.actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredReports.length > 0 ? (
                  filteredReports.map((report) => (
                    <TableRow key={report.id} hover>
                      <TableCell>{report.id}</TableCell>
                      <TableCell>{formatDate(report.date)}</TableCell>
                      <TableCell>{report.createdBy}</TableCell>
                      <TableCell>
                        <Chip
                          label={t(`status_${report.status}`)}
                          color={getStatusColor(report.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {report.equipmentCount || 0} {t('nav_equipment')}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title={t('actions_view')}>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleViewReport(report.id)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('actions_download_pdf')}>
                            <IconButton 
                              size="small" 
                              color="secondary"
                              onClick={() => handleDownloadPDF(report.id)}
                            >
                              <Download />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('actions_remove')}>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(report.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {t('archive.no_reports')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('archive.no_reports_desc')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TechnicianReports;